"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useSubscription } from "@/hooks/useSubscription";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckIcon, XIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChecklistStep {
  id: string;
  label: string;
  description: string;
  complete: boolean;
  /** Optional link target when the step is incomplete */
  href?: string;
  /** CTA label for the link */
  cta?: string;
}

const DISMISSED_KEY = "blocky:onboarding-checklist-dismissed";

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total === 0 ? 100 : Math.round((completed / total) * 100);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted">
        <span>{completed} of {total} steps complete</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/10">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Single step row ──────────────────────────────────────────────────────────

function StepRow({ step }: { step: ChecklistStep }) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors",
        step.complete
          ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20"
          : "border-border bg-background"
      )}
    >
      {/* Status icon */}
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
          step.complete
            ? "bg-green-500 text-white"
            : "border-2 border-muted/40"
        )}
      >
        {step.complete && <CheckIcon className="h-3 w-3" />}
      </span>

      {/* Label + description */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "font-medium leading-tight",
            step.complete ? "text-green-800 line-through dark:text-green-300" : "text-foreground"
          )}
        >
          {step.label}
        </p>
        {!step.complete && (
          <p className="mt-0.5 text-xs text-muted">{step.description}</p>
        )}
      </div>

      {/* CTA button when incomplete */}
      {!step.complete && step.href && step.cta && (
        <Link href={step.href} className="shrink-0">
          <Button size="sm" variant="outline" className="text-xs">
            {step.cta}
          </Button>
        </Link>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * OnboardingChecklist
 *
 * Guides new BlockyNetworks subscribers through the core post-payment setup
 * steps. Renders only when the user has an active or trialing subscription
 * and at least one step is still incomplete. Auto-hides when all steps are
 * done, or when the user explicitly dismisses it.
 *
 * Step completion is derived reactively from live Convex queries — no
 * additional backend mutations required.
 */
export function OnboardingChecklist() {
  const { hasAccess, isTrialing, isLoading: subLoading } = useSubscription();

  const user = useQuery(api.users.getCurrentUser);
  const ownedOrgs = useQuery(api.organizations.getOwned);

  // ── localStorage-based dismissal ──
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISSED_KEY) === "1");
    } catch {
      // localStorage not available (SSR / incognito) — default to not dismissed
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // best-effort
    }
  };

  // ── Loading state ──
  const isLoading = subLoading || user === undefined || ownedOrgs === undefined;

  // ── Only show for paid/trialing users ──
  const shouldShow = !subLoading && (hasAccess || isTrialing);
  if (!shouldShow || dismissed) return null;

  // ── Derive checklist state from Convex data ──
  const hasUsername = !!user?.username;
  const hasOrg = (ownedOrgs?.length ?? 0) > 0;

  // Any owned org that has a linked game server (plugin registered via heartbeat)
  const hasLinkedServer =
    ownedOrgs?.some((org: any) => !!org.serverId) ?? false;

  // Any owned org with a public server listing
  const hasPublicServer =
    ownedOrgs?.some((org: any) => org.visibility === "public") ?? false;

  const steps: ChecklistStep[] = [
    {
      id: "subscribe",
      label: "Activate your subscription",
      description: "You're all set — your BlockyNetworks plan is active.",
      complete: true, // always done if we reach this component
    },
    {
      id: "username",
      label: "Set your username",
      description:
        "Choose the handle other players will see in chat and on your public profile.",
      complete: hasUsername,
      href: hasUsername ? undefined : "/onboarding",
      cta: "Set Username",
    },
    {
      id: "create-org",
      label: "Create your first community",
      description:
        "Set up an organization to represent your Hytale server or network.",
      complete: hasOrg,
      href: hasOrg ? undefined : "/dashboard/my-orgs/new",
      cta: "Create Community",
    },
    {
      id: "link-server",
      label: "Connect your game server",
      description:
        "Install the BlockyNetworks plugin on your Hytale server so heartbeats and player data sync in real-time.",
      complete: hasLinkedServer,
      href: hasLinkedServer ? undefined : "/downloads",
      cta: "Get Plugin",
    },
    {
      id: "go-public",
      label: "List your server publicly",
      description:
        "Make your community discoverable in the Server Finder so players can find and join you.",
      complete: hasPublicServer,
      href: hasPublicServer ? undefined : (hasOrg ? "/dashboard/my-orgs" : "/dashboard/my-orgs/new"),
      cta: "Go Public",
    },
  ];

  const completedCount = steps.filter((s) => s.complete).length;
  const allDone = completedCount === steps.length;

  // ── If all steps complete, auto-dismiss after showing a brief success state ──
  useEffect(() => {
    if (allDone && !dismissed) {
      const t = setTimeout(() => handleDismiss(), 6000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  if (dismissed) return null;

  return (
    <Card className="mb-8 border-primary/30 bg-primary/[0.03]">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <CardTitle className="text-base">
              {allDone ? "Setup complete!" : "Get started"}
            </CardTitle>
            <CardDescription>
              {allDone
                ? "You've completed all setup steps. Your community is ready for players."
                : "Complete these steps to get your Hytale server community up and running."}
            </CardDescription>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss checklist"
            className="mt-0.5 rounded p-1 text-muted hover:bg-foreground/5 hover:text-foreground transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Progress bar — hide when loading */}
        {!isLoading && (
          <div className="mt-3">
            <ProgressBar completed={completedCount} total={steps.length} />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-2">
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-lg bg-foreground/5"
              />
            ))}
          </div>
        ) : (
          <>
            {steps.map((step) => (
              <StepRow key={step.id} step={step} />
            ))}

            {allDone && (
              <div className="pt-1 text-center text-sm text-green-700 dark:text-green-400">
                Checklist will auto-hide in a moment.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
